import { ProfileCircle as ProfileCircleIcon } from 'iconoir-react'
import moment from 'moment'
import { ReactElement } from 'react'
import type { Validator } from 'remix-validated-form'
import Button from '../Button'
import CircularProgressBar from '../CircularProgressBar'
import FormComboAsyncV2 from '../form/FormComboAsyncV2'
import FormDatePicker from '../form/FormDatePicker'
import { FormMarkDownEditor } from '../form/FormMarkDownEditor'
import { SmartForm } from '../form/SmartForm'
import { Toggle } from '../Toggle'
import { staffMembersFetcher } from 'app/lib/fetchers/staffMembersFetcher'
import type { ContactJson } from 'app/types/entities'
import type { FollowUpJson } from 'app/types/entities'
import { OptionType } from 'types/OptionType'

export type FollowUpFormProps = {
  contact: ContactJson
  followUp?: FollowUpJson
  leadStatus?: ContactJson[`leadStatus`]
  progressOpen: boolean
  progress: number
  staff: OptionType | undefined
  defaultValues: Record<string, string | undefined>
  scheduledDisabled?: boolean
  appointmentDateDisabled: boolean
  isConnected?: boolean
  isScheduled?: boolean
  hiddenInputs: Array<{ name: string; value: string | undefined }>
  saving: boolean
  validator: Validator<Record<string, unknown>>
  setStaff: (staff: OptionType) => void
  submitFollowUp: (data?: Record<string, unknown>) => Promise<void>
  deleteHandler: () => Promise<void>
  handleIsConnectedChange: (value: string | boolean) => void
  handleIsScheduledChange: (value: string | boolean) => void
  onComplete: () => void
  progressStart: () => void
  setProgressOpen: (open: boolean) => void
}

export function FollowUpFormV2({
  followUp,
  leadStatus,
  progressOpen,
  progress,
  saving,
  staff,
  defaultValues,
  appointmentDateDisabled,
  isConnected,
  isScheduled,
  hiddenInputs,
  setStaff,
  submitFollowUp,
  deleteHandler,
  handleIsConnectedChange,
  handleIsScheduledChange,
  onComplete,
  progressStart,
  setProgressOpen,
  validator,
}: FollowUpFormProps): ReactElement {
  return progressOpen ? (
    <div className="flex flex-col items-center gap-y-6 py-4 align-middle">
      <CircularProgressBar progress={progress} />
      <p className="px-6 text-body-sm text-default">
        Updating in progress. This may take up to 30 seconds. We appreciate your patience.
      </p>
    </div>
  ) : (
    <SmartForm
      id="follow-up-form"
      validator={validator}
      defaultValues={defaultValues}
      className="w-full md:w-[600px] !gap-y-3.5 px-6"
      hiddenInputs={hiddenInputs}
      onSubmit={submitFollowUp}
      onCancel={onComplete}
      hideButtons
      preventSubmit
      showDelete
    >
      {leadStatus ? (
        <>
          {leadStatus === `Scheduled` && (
            <>
              <FormComboAsyncV2
                inputClassName="!text-sm !font-normal !leading-5 !text-default"
                labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
                value={staff}
                optionsMapper={(options) => {
                  if (options.total > 0) {
                    return options.hits.map((option) => ({ id: option.id, name: option.name, value: option.id }))
                  }
                  return []
                }}
                onChange={(value) => setStaff(value)}
                name="owner"
                cols={6}
                label={`Scheduled by`}
                fetcher={staffMembersFetcher}
                customIcon={<ProfileCircleIcon className="h-5 w-5" />}
              />
              <FormDatePicker
                labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
                label="Scheduled Appointment Date"
                name="appointmentDate"
                disabled={appointmentDateDisabled}
                value={
                  defaultValues.isScheduled === `true`
                    ? moment.utc(defaultValues.appointmentDate).format(`YYYY-MM-DD`)
                    : undefined
                }
                cols={6}
              />
            </>
          )}
          {leadStatus === `In Progress` && (
            <>
              <div className="col-span-6 flex items-center justify-between py-2">
                <span className="text-sm font-medium text-default">Did you connect?</span>
                <input type="hidden" name="isConnected" value={isConnected ? `true` : `false`} />
                <div className="flex items-center gap-2">
                  <Toggle
                    defaultValue={isConnected}
                    handleToggle={(value) => handleIsConnectedChange(value)}
                    size="md"
                  />
                  <span className="w-6 text-sm font-normal text-default">{isConnected ? `Yes` : `No`}</span>
                </div>
              </div>
              <FormComboAsyncV2
                inputClassName="!text-sm !font-normal !leading-5 !text-default"
                labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
                value={staff}
                optionsMapper={(options) => {
                  if (options.total > 0) {
                    return options.hits.map((option) => ({ id: option.id, name: option.name, value: option.id }))
                  }
                  return []
                }}
                onChange={(value) => setStaff(value)}
                name="owner"
                cols={6}
                label="Followed up by"
                fetcher={staffMembersFetcher}
                customIcon={<ProfileCircleIcon className="h-5 w-5" />}
              />
            </>
          )}
        </>
      ) : (
        <>
          <FormComboAsyncV2
            inputClassName="!text-sm !font-normal !leading-5 !text-default"
            labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
            value={staff}
            optionsMapper={(options) => {
              if (options.total > 0) {
                let staff = [...options.hits]
                return staff.map((option) => ({ id: option.id, name: option.name, value: option.name }))
              }
              return []
            }}
            name="owner"
            cols={6}
            label="Followed up by"
            newLineError={false}
            fetcher={staffMembersFetcher}
            onChange={(value) => setStaff(value)}
            showErrorIcon
            required
            customIcon={<ProfileCircleIcon className="h-5 w-5" />}
          />
          <FormDatePicker
            labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
            name="followUpDate"
            label="Follow up date"
            value={defaultValues.followUpDate || new Date().toISOString()}
            allowFutureDates={false}
            cols={6}
          />
          <div className="col-span-6 flex items-center justify-between py-2">
            <span className="text-sm font-medium text-default">Did you connect?</span>
            <input type="hidden" name="isConnected" value={isConnected ? `true` : `false`} />
            <div className="flex items-center gap-2">
              <Toggle defaultValue={isConnected} handleToggle={(value) => handleIsConnectedChange(value)} size="md" />
              <span className="w-6 text-sm font-normal text-default">{isConnected ? `Yes` : `No`}</span>
            </div>
          </div>
          <div className="col-span-6 flex items-center justify-between py-2">
            <span className="text-sm font-medium text-default">Did you schedule?</span>
            <input type="hidden" name="isScheduled" value={isScheduled ? `true` : `false`} />
            <div className="flex items-center gap-2">
              <Toggle
                key={`isScheduled-${isConnected}-${isScheduled}`}
                defaultValue={isScheduled}
                handleToggle={(value) => {
                  const result = handleIsScheduledChange(value)
                  return result
                }}
                size="md"
                controlled={true}
              />
              <span className="w-6 text-sm font-normal text-default">{isScheduled ? `Yes` : `No`}</span>
            </div>
          </div>
          {isScheduled && (
            <FormDatePicker
              labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
              label="Scheduled Appointment Date"
              name="appointmentDate"
              disabled={appointmentDateDisabled}
              value={
                defaultValues.isScheduled === `true`
                  ? moment.utc(defaultValues.appointmentDate).format(`YYYY-MM-DD`)
                  : undefined
              }
              cols={6}
            />
          )}
        </>
      )}
      <FormMarkDownEditor
        name="description"
        label="Note"
        richTextEditorNamespace="Add-Follow-Up"
        labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
        placeholder="Type your note here..."
        errorClassName="text-red-500"
        cols={6}
        editable
      />
      <div
        className={`col-span-6 -mx-6 mt-4 flex items-center border-t border-default px-6 py-3 ${
          followUp ? `justify-between` : `justify-end`
        }`}
      >
        {followUp && (
          <Button
            label="Delete"
            size="md"
            mode="danger"
            loading={saving}
            onClick={async () => {
              setProgressOpen(true)
              progressStart()
              await deleteHandler()
            }}
          />
        )}
        <div className="flex items-center gap-2">
          <Button label="Cancel" mode="white" size="md" onClick={onComplete} disabled={saving} />
          <Button type="submit" label="Save" size="md" mode="primary" loading={saving} />
        </div>
      </div>
    </SmartForm>
  )
}
